import { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { useRefresh } from '../../../hooks';
import { deleteResource, updateResource, useDispatch } from '../../../state';
import { makeStyles } from '../../../themes';
import { FormModal } from './FormModal';
import { InfoMessage } from '../InfoMessage';
import { TextInput } from '../inputs';

interface EditPositionProps {
  open: boolean;
  handleClose: () => any;
  position: number | string;
  warehouseId: string | number;
  aisleId: number;
  aisleName: string;
  rowId: number;
  setViewVersion?: React.Dispatch<React.SetStateAction<number>>;
}

const editInitialState = {
  warehouseAislePosition: '',
};

export function EditWarehouseAislePositionForm({
  open,
  handleClose,
  warehouseId,
  aisleId,
  aisleName,
  rowId,
  position,
  setViewVersion,
}: EditPositionProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(editInitialState);

  useEffect(() => {
    setState((cur) => ({ ...cur, warehouseAislePosition: position as string }));
  }, [position]);

  const handleSubmit = useCallback(
    async (values) => {
      setSubmitting(true);
      const payload = {
        ...values,
        warehouseAisleID: aisleId,
        warehouseID: warehouseId,
      };
      const { error } = await dispatch(
        updateResource({
          baseUrl: `/warehouses/${warehouseId}/aisles/${aisleId}/rows/${rowId}/positions/${position}`,
          payload,
          message: 'Changes saved',
        }),
      );
      setSubmitting(false);
      if (!error) {
        setViewVersion ? setViewVersion((cur) => ++cur) : refresh();
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aisleId, warehouseId, rowId, position],
  );

  const handleDelete = useCallback(async () => {
    setSubmitting(true);
    const response = await dispatch(
      deleteResource({
        baseUrl: '/warehouses',
        id: warehouseId,
        path: `aisles/${aisleId}/rows/${rowId}/positions/${position}`,
      }),
    );
    setSubmitting(false);
    const { error } = response;
    if (!error) {
      setViewVersion ? setViewVersion((cur) => ++cur) : refresh();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, aisleId, rowId, position]);

  const formik = useFormik({
    initialValues: { ...editInitialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: editPositionSchema,
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={
        <span
          className={classes.title}
        >{`Edit aisle ${aisleName} position ${position}`}</span>
      }
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 520 } }}
      submitting={submitting}
      isEdit={!!aisleId}
      handleDelete={handleDelete}
    >
      <InfoMessage message='Editing the position will update all locations that were setup in this position.' />
      <TextInput
        name='warehouseAislePosition'
        formikProps={formik}
        label='Position'
        className={classes.input}
      />
    </FormModal>
  );
}

const useStyles = makeStyles({ name: { EditWarehouseAislePositionForm } })(
  (theme) => ({
    title: {
      fontFamily: 'Montserrat',
      fontSize: 20,
      fontWeight: 600,
    },
    input: {
      marginBottom: 16,
      width: '100%',
    },
  }),
);

const editPositionSchema = object().shape({
  warehouseAislePosition: string().required('Required'),
});
